import { gql, useMutation } from '@apollo/client';
import type { EventFeaturedUser, Event, User } from '@shady-pines/db';
import { type Uuid, now } from '@shady-pines/types';
import { useCallback } from 'react';
import type { MutationResponse } from '../types';

const CREATE_EVENT_FEATURED_USERS = gql`
  mutation CreateEventFeaturedUsers($objects: [event_featured_usersInsertInput!]!) {
    insertIntoevent_featured_usersCollection(objects: $objects) {
      records {
        __typename
        id
        event_id
        user_id
      }
    }
  }
`;

export const useCreateEventFeaturedUsers = () => {
  const [createFeaturedUsers, { loading, error }] = useMutation<
    MutationResponse<'insertIntoevent_featured_usersCollection', EventFeaturedUser>
  >(CREATE_EVENT_FEATURED_USERS, {
    refetchQueries: ['GetEvent', 'GetEvents'],
  });

  const create = useCallback(
    async (input: { eventId: Uuid<Event>; userIds: Uuid<User>[] }) => {
      const objects = input.userIds.map((userId) => ({
        event_id: input.eventId,
        user_id: userId,
      }));

      return createFeaturedUsers({
        variables: {
          objects,
        },
      });
    },
    [createFeaturedUsers],
  );

  return {
    createEventFeaturedUsers: create,
    loading,
    error,
  };
};

const DELETE_EVENT_FEATURED_USERS = gql`
  mutation DeleteEventFeaturedUsers($ids: [UUID!]!, $deletedAt: Timestamp!) {
    updateevent_featured_usersCollection(
      set: { deleted_at: $deletedAt }
      filter: { id: { in: $ids } }
    ) {
      records {
        __typename
        id
        deleted_at
      }
    }
  }
`;

export const useDeleteEventFeaturedUsers = () => {
  const [deleteMapping, { loading, error }] = useMutation<
    MutationResponse<'updateevent_featured_usersCollection', EventFeaturedUser>
  >(DELETE_EVENT_FEATURED_USERS, {
    refetchQueries: ['GetEvent', 'GetEvents'],
  });

  const deleteEventFeaturedUsers = useCallback(
    (ids: Uuid<EventFeaturedUser>[] | Uuid<EventFeaturedUser>) => {
      const idsArray = Array.isArray(ids) ? ids : [ids];

      if (!idsArray.length) {
        return Promise.resolve();
      }

      return deleteMapping({
        variables: {
          ids: idsArray,
          deletedAt: now(),
        },
      });
    },
    [deleteMapping],
  );

  return {
    deleteEventFeaturedUsers,
    loading,
    error,
  };
};
